import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { makeStyles, alpha } from '@material-ui/core/styles';

import { MONO, manewcoTokens } from '@platform/plugin-platform-react';

/**
 * Scaffolder brand restyle — sidecar component.
 *
 * Backstage's `<ScaffolderPage>` is a routable extension and must be
 * rendered DIRECTLY as the `element` of its route — wrapping it in a
 * styling container breaks routable discovery and crashes the app with
 * "Routable extension components may not be rendered by other components".
 *
 * So we use a sidecar pattern: this component is mounted unconditionally
 * (inside <Root>), watches the current route, and toggles a
 * `platform-scaffolder` class on `document.body` whenever the user is on
 * a `/create*` page. All our `@global` overrides are scoped to that body
 * class so they only apply on the scaffolder, never leaking elsewhere.
 *
 * Aesthetic direction: editorial-technical, anchored on the secondary/brand
 * accent (`theme.palette.secondary.main`, historically "pink").
 * Pink is the IDP's "creation" accent — distinct from purple (databases),
 * Redis red (cache) and blue (ops). Cards stagger-in on mount; the stepper
 * uses numbered mono circles; form fields focus to pink; primary buttons
 * are solid pink.
 */
const useStyles = makeStyles(theme => {
  const tokens = manewcoTokens(theme);
  return {
    '@global': {
      '@keyframes scaffStagger': {
        from: { opacity: 0, transform: 'translateY(10px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
      },

      'body.platform-scaffolder': {
        background: `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
      },

      // ── Page chrome — Backstage's <Header> + <Content> ───────────────
      // Soften the default purple gradient header to match the rest of the
      // IDP — keep the same shape but tone it down.
      'body.platform-scaffolder [class*="BackstageHeader-header-"]': {
        boxShadow: 'none',
      },
      'body.platform-scaffolder [class*="BackstageHeader-title-"]': {
        fontWeight: 800,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        fontSize: '1.5rem',
      },
      'body.platform-scaffolder [class*="BackstageHeader-subtitle-"]': {
        fontSize: '0.86rem',
        opacity: 0.85,
      },
      'body.platform-scaffolder [class*="BackstageContent-root-"]': {
        padding: '24px 32px 48px',
      },

      // ── Cards (template gallery + form panels) ──────────────────────
      'body.platform-scaffolder .MuiCard-root': {
        borderRadius: 14,
        border: `1px solid ${theme.palette.divider}`,
        borderLeft: `4px solid ${theme.palette.secondary.main}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        animation: 'scaffStagger 0.4s ease both',
        background: theme.palette.background.paper,
        overflow: 'hidden',
      },
      'body.platform-scaffolder .MuiCard-root:hover': {
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        transform: 'translateY(-2px)',
      },
      'body.platform-scaffolder .MuiCardMedia-root': {
        display: 'none', // hide template banner image — we don't have brand banners
      },
      'body.platform-scaffolder .MuiCardContent-root': {
        padding: '20px 22px 12px',
      },
      'body.platform-scaffolder .MuiCardActions-root': {
        padding: '8px 22px 16px',
      },
      // Stagger reveal across the first cards of a grid
      'body.platform-scaffolder .MuiGrid-item:nth-child(2) .MuiCard-root': {
        animationDelay: '0.05s',
      },
      'body.platform-scaffolder .MuiGrid-item:nth-child(3) .MuiCard-root': {
        animationDelay: '0.10s',
      },
      'body.platform-scaffolder .MuiGrid-item:nth-child(4) .MuiCard-root': {
        animationDelay: '0.15s',
      },
      'body.platform-scaffolder .MuiGrid-item:nth-child(5) .MuiCard-root': {
        animationDelay: '0.20s',
      },
      'body.platform-scaffolder .MuiGrid-item:nth-child(6) .MuiCard-root': {
        animationDelay: '0.25s',
      },

      // Page title in <Header>
      'body.platform-scaffolder header h1, body.platform-scaffolder [class*="BackstageHeader-title"]':
        {
          fontWeight: 800,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          fontSize: '1.4rem',
        },

      // Card title + subheader
      'body.platform-scaffolder .MuiCardHeader-title': {
        fontWeight: 800,
        fontSize: '0.95rem',
        letterSpacing: '0.01em',
        color: theme.palette.text.primary,
      },
      'body.platform-scaffolder .MuiCardHeader-subheader': {
        fontSize: '0.78rem',
        color: theme.palette.text.secondary,
      },

      // Tags / chips
      'body.platform-scaffolder .MuiChip-root': {
        borderRadius: 6,
        fontFamily: MONO,
        fontSize: '0.68rem',
        fontWeight: 700,
        height: 22,
        letterSpacing: '0.02em',
        background: theme.palette.secondary.light,
        color: theme.palette.secondary.main,
        border: 'none',
      },
      'body.platform-scaffolder .MuiChip-outlined': {
        background: 'transparent',
        border: `1px solid ${theme.palette.divider}`,
        color: theme.palette.text.secondary,
      },

      // ── Stepper ────────────────────────────────────────────────────
      'body.platform-scaffolder .MuiStepper-root': {
        background: 'transparent',
        padding: '24px 0',
      },
      'body.platform-scaffolder .MuiStepLabel-label': {
        fontWeight: 700,
        fontSize: '0.74rem',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: theme.palette.text.secondary,
      },
      'body.platform-scaffolder .MuiStepLabel-label.MuiStepLabel-active, body.platform-scaffolder .MuiStepLabel-label.MuiStepLabel-completed':
        {
          color: theme.palette.text.primary,
        },
      'body.platform-scaffolder .MuiStepIcon-root': {
        color: theme.palette.divider,
        width: 26,
        height: 26,
      },
      'body.platform-scaffolder .MuiStepIcon-root.MuiStepIcon-active, body.platform-scaffolder .MuiStepIcon-root.MuiStepIcon-completed':
        {
          color: theme.palette.secondary.main,
        },
      'body.platform-scaffolder .MuiStepIcon-text': {
        fontFamily: MONO,
        fontWeight: 800,
        fontSize: '0.7rem',
        fill: theme.palette.secondary.contrastText,
      },
      'body.platform-scaffolder .MuiStepConnector-line': {
        borderColor: theme.palette.divider,
        borderTopWidth: 2,
      },
      'body.platform-scaffolder .MuiStepConnector-active .MuiStepConnector-line, body.platform-scaffolder .MuiStepConnector-completed .MuiStepConnector-line':
        {
          borderColor: theme.palette.secondary.main,
        },

      // ── Inputs ─────────────────────────────────────────────────────
      'body.platform-scaffolder .MuiInputLabel-root': {
        fontSize: '0.78rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
        letterSpacing: '0.02em',
      },
      'body.platform-scaffolder .MuiInputLabel-shrink': {
        fontSize: '0.86rem',
        fontWeight: 700,
      },
      'body.platform-scaffolder .MuiOutlinedInput-root': {
        borderRadius: 10,
        background: theme.palette.background.paper,
        transition: 'border-color 0.15s, box-shadow 0.15s',
      },
      'body.platform-scaffolder .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline':
        {
          borderColor: theme.palette.divider,
        },
      'body.platform-scaffolder .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
        {
          borderColor: theme.palette.secondary.main,
        },
      'body.platform-scaffolder .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
        {
          borderColor: theme.palette.secondary.main,
          borderWidth: 2,
        },
      'body.platform-scaffolder .MuiOutlinedInput-input': {
        fontFamily: MONO,
        fontSize: '0.86rem',
        color: theme.palette.text.primary,
        padding: '14px 14px',
      },
      'body.platform-scaffolder .MuiFormHelperText-root': {
        fontSize: '0.72rem',
        color: theme.palette.text.secondary,
        marginLeft: 0,
        marginTop: 6,
      },

      // Field labels (rjsf field titles)
      'body.platform-scaffolder .field-string > label, body.platform-scaffolder .field-number > label, body.platform-scaffolder .field-boolean > label, body.platform-scaffolder .field-object > label':
        {
          fontWeight: 800,
          fontSize: '0.7rem',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: theme.palette.text.primary,
          display: 'block',
          marginBottom: 6,
        },

      // ── Buttons ────────────────────────────────────────────────────
      'body.platform-scaffolder .MuiButton-root': {
        textTransform: 'none',
        borderRadius: 10,
        fontWeight: 700,
        fontSize: '0.82rem',
        letterSpacing: '0.01em',
        padding: '8px 20px',
      },
      'body.platform-scaffolder .MuiButton-contained, body.platform-scaffolder .MuiButton-containedPrimary':
        {
          background: theme.palette.secondary.main,
          color: theme.palette.secondary.contrastText,
          boxShadow: 'none',
        },
      'body.platform-scaffolder .MuiButton-contained:hover, body.platform-scaffolder .MuiButton-containedPrimary:hover':
        {
          background: theme.palette.secondary.dark,
          boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.35)}`,
        },
      'body.platform-scaffolder .MuiButton-contained.Mui-disabled': {
        background: theme.palette.divider,
        color: theme.palette.secondary.contrastText,
      },
      'body.platform-scaffolder .MuiButton-outlined': {
        borderColor: theme.palette.divider,
        color: theme.palette.text.secondary,
      },
      'body.platform-scaffolder .MuiButton-outlined:hover': {
        borderColor: theme.palette.secondary.main,
        color: theme.palette.secondary.main,
        background: theme.palette.secondary.light,
      },
      'body.platform-scaffolder .MuiButton-text': {
        color: theme.palette.text.secondary,
      },
      'body.platform-scaffolder .MuiButton-text:hover': {
        color: theme.palette.secondary.main,
        background: 'transparent',
      },

      // ── Code / JSON preview ────────────────────────────────────────
      'body.platform-scaffolder pre, body.platform-scaffolder code': {
        fontFamily: MONO,
        fontSize: '0.78rem',
        background: theme.palette.background.default,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        padding: '12px 14px',
        color: theme.palette.text.primary,
      },

      // ── Section titles inside the wizard ───────────────────────────
      'body.platform-scaffolder legend, body.platform-scaffolder h5, body.platform-scaffolder h6':
        {
          fontWeight: 800,
          fontSize: '0.78rem',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: theme.palette.text.primary,
          marginBottom: 12,
          paddingBottom: 8,
          borderBottom: `2px solid ${theme.palette.secondary.light}`,
        },

      // ── Tabs (template categories) ─────────────────────────────────
      'body.platform-scaffolder .MuiTabs-root': {
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
      'body.platform-scaffolder .MuiTabs-root .MuiTab-root': {
        textTransform: 'uppercase',
        fontWeight: 700,
        fontSize: '0.74rem',
        letterSpacing: '0.06em',
        minHeight: 46,
      },
      'body.platform-scaffolder .MuiTabs-root .MuiTab-root.Mui-selected': {
        color: theme.palette.secondary.main,
      },
      'body.platform-scaffolder .MuiTabs-indicator': {
        backgroundColor: theme.palette.secondary.main,
        height: 3,
        borderRadius: '3px 3px 0 0',
      },

      // ── Errors ─────────────────────────────────────────────────────
      'body.platform-scaffolder .MuiFormHelperText-root.Mui-error, body.platform-scaffolder .field-error':
        {
          color: theme.palette.error.main,
          fontWeight: 600,
        },
      'body.platform-scaffolder .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline':
        {
          borderColor: theme.palette.error.main,
        },

      // ── Alerts on success page ─────────────────────────────────────
      'body.platform-scaffolder .MuiAlert-root': {
        borderRadius: 10,
        fontFamily: MONO,
        fontSize: '0.82rem',
      },

      // ── Workflow / wizard navigation footer ───────────────────────
      // The bottom-right "Next" / "Back" button row.
      'body.platform-scaffolder .MuiButton-root + .MuiButton-root': {
        marginLeft: 8,
      },
      // Backstage scaffolder-react wraps form actions in a flex container.
      // Make sure the spacing matches the rest of the IDP.
      'body.platform-scaffolder form .MuiButton-root': {
        minWidth: 110,
      },

      // ── Review step (JSON of all form values) ─────────────────────
      'body.platform-scaffolder [class*="ReviewStep"] dt': {
        fontWeight: 800,
        fontSize: '0.7rem',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: theme.palette.text.secondary,
        marginBottom: 4,
      },
      'body.platform-scaffolder [class*="ReviewStep"] dd': {
        fontFamily: MONO,
        fontSize: '0.84rem',
        color: theme.palette.text.primary,
        marginLeft: 0,
        marginBottom: 14,
      },

      // ── Task page (after submit) — log stream ─────────────────────
      // NOTE: dark terminal chrome below is intentionally NOT retheme'd
      // (same convention as EnvManagerContent/BuildContent — see spec).
      'body.platform-scaffolder pre[class*="Log"], body.platform-scaffolder [class*="LogStream"] pre': {
        background: tokens.console.deepBackground,
        color: tokens.console.deepText,
        borderColor: tokens.console.deepBackground,
        fontSize: '0.74rem',
        lineHeight: 1.6,
        maxHeight: 480,
        overflow: 'auto',
      },

      // Hide the default rjsf object title that would render above our custom
      // PlatformLayout (the layout already shows its own header).
      'body.platform-scaffolder .field-object > fieldset > legend': {
        display: 'none',
      },
    },
  };
});

export const ScaffolderStyles = () => {
  // Activates the brand restyle by applying a class to <body> on /create*.
  // Returns nothing — purely a side-effect component.
  useStyles();
  const { pathname } = useLocation();

  useEffect(() => {
    const isScaffolder = pathname.startsWith('/create');
    if (isScaffolder) {
      document.body.classList.add('platform-scaffolder');
      return () => document.body.classList.remove('platform-scaffolder');
    }
    return undefined;
  }, [pathname]);

  return null;
};
